import {
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  ReloadOutlined,
  StopOutlined,
} from '@ant-design/icons'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Input, message, Table, Tag, Tooltip } from 'antd'
import type { ColumnsType, SorterResult, TablePaginationConfig } from 'antd/es/table/interface'
import { FilterValue } from 'antd/es/table/interface'
import dayjs from 'dayjs'
import { PagingParams, PartnerReferralWhitelistItem, PartnerWhitelistUploadResponse } from 'Modules'
import { useEffect, useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import LinkAddress from 'component/link/link-address'
import { FEATURE } from 'constants/feature'
import {
  PARTNER_REFERRAL_MESSAGES,
  PARTNER_SEARCH_MAX_LENGTH,
  PartnerReferralStatus,
} from 'constants/partner-referral'
import { useDebounce } from 'hooks/use-debounce'
import { usePermission } from 'hooks/use-permission'
import useSearchParams from 'hooks/use-search-params'
import { downloadBlob } from 'utils/download-file'
import { logError } from 'utils/logger'

import { ModalPartnerTransactions } from './modal-partner-transactions'
import { PartnerActionModal, PartnerActionType } from './partner-action-modal'
import { PartnerWhitelistUpload } from './partner-whitelist-upload'

interface PartnerUsersParams extends PagingParams {
  keyword?: string
  sort?: string
}

// Map antd column keys to backend sort fields (doc #12).
const SORT_FIELD_MAP: Record<string, string> = {
  totalReferral: 'totalReferral',
  totalEarned: 'totalEarned',
  totalClaimed: 'totalClaimed',
  createdAt: 'createdAt',
}

const getTotalReferrals = (record: PartnerReferralWhitelistItem) => record.totalReferral ?? 0

export const PartnerUsersTable = () => {
  const queryClient = useQueryClient()
  const { searchParams, setSearchParams, initPagination, onSearch } =
    useSearchParams<PartnerUsersParams>({ sort: 'createdAt:desc' })
  const { canCRUD } = usePermission()
  const canEdit = canCRUD(FEATURE.PTO_REFERRAL)

  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebounce(searchInput, 400) as string

  const [actionType, setActionType] = useState<PartnerActionType>('deactivate')
  const [actionRecord, setActionRecord] = useState<PartnerReferralWhitelistItem | null>(null)
  const [actionLoading, setActionLoading] = useState(false)

  // Last successful upload — kept so the imported batch can be exported (doc #8).
  const [lastUpload, setLastUpload] = useState<PartnerWhitelistUploadResponse | null>(null)
  const [exporting, setExporting] = useState(false)

  // Partner whose referral transactions are shown in the performance modal.
  const [performanceAddress, setPerformanceAddress] = useState<string | undefined>()

  // Sync the debounced search term into query params (resets to page 1).
  useEffect(() => {
    onSearch({ keyword: debouncedSearch.trim() || undefined } as PartnerUsersParams)
  }, [debouncedSearch])

  const { data, isLoading } = useQuery({
    queryKey: ['partnerReferralWhitelist', searchParams],
    queryFn: async () => {
      const res = await AppAdmin.getPartnerReferralWhitelist(searchParams)
      const payload = res.data?.data
      return {
        results: (payload?.results ?? []) as PartnerReferralWhitelistItem[],
        total: payload?.pagination?.totalResults ?? payload?.totalResults ?? 0,
      }
    },
    gcTime: 30000,
  })

  const users = data?.results ?? []
  const total = data?.total ?? 0

  const refetch = () => queryClient.invalidateQueries({ queryKey: ['partnerReferralWhitelist'] })

  const handleUploaded = (result?: PartnerWhitelistUploadResponse) => {
    if (result) setLastUpload(result)
    refetch()
  }

  const handleExportUpload = async () => {
    if (!lastUpload?.uploadId) return
    try {
      setExporting(true)
      const res = await AppAdmin.exportPartnerReferralWhitelist(lastUpload.uploadId)
      downloadBlob(res.data, lastUpload.fileName || `partner_whitelist_${lastUpload.uploadId}.csv`)
    } catch (error) {
      logError('Export partner whitelist failed', error)
      message.error('Failed to export whitelist')
    } finally {
      setExporting(false)
    }
  }

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter:
      | SorterResult<PartnerReferralWhitelistItem>
      | SorterResult<PartnerReferralWhitelistItem>[]
  ) => {
    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter
    const sortField = singleSorter?.columnKey
      ? SORT_FIELD_MAP[singleSorter.columnKey as string]
      : undefined
    const sortOrder = singleSorter?.order
      ? singleSorter.order === 'ascend'
        ? 'asc'
        : 'desc'
      : undefined
    const sort = sortField && sortOrder ? `${sortField}:${sortOrder}` : 'createdAt:desc'

    setSearchParams((pre) => ({
      ...pre,
      page: pagination.current,
      limit: pagination.pageSize,
      sort,
    }))
  }

  const updateStatus = async (
    record: PartnerReferralWhitelistItem,
    status: PartnerReferralStatus,
    successMessage: string
  ) => {
    try {
      setActionLoading(true)
      await AppAdmin.updatePartnerReferralStatus(record.id, status)
      message.success(successMessage)
      setActionRecord(null)
      refetch()
    } catch (error) {
      logError('Update partner status failed', error)
      message.error('Failed to update partner status')
    } finally {
      setActionLoading(false)
    }
  }

  const openModal = (type: PartnerActionType, record: PartnerReferralWhitelistItem) => {
    setActionType(type)
    setActionRecord(record)
  }

  const handleConfirmAction = () => {
    if (!actionRecord) return
    if (actionType === 'deactivate') {
      updateStatus(actionRecord, PartnerReferralStatus.DEACTIVATED, PARTNER_REFERRAL_MESSAGES.S01)
    } else {
      updateStatus(actionRecord, PartnerReferralStatus.DELETE, PARTNER_REFERRAL_MESSAGES.S03)
    }
  }

  const handleReactivate = (record: PartnerReferralWhitelistItem) =>
    updateStatus(record, PartnerReferralStatus.ACTIVE, PARTNER_REFERRAL_MESSAGES.S02)

  const columns: ColumnsType<PartnerReferralWhitelistItem> = [
    {
      title: 'Wallet Address',
      dataIndex: 'walletAddress',
      key: 'walletAddress',
      width: 240,
      render: (text: string, record) => (
        <Flex align="center" gap={8} wrap="wrap">
          <LinkAddress address={text} type="address" />
          {record.status === PartnerReferralStatus.DEACTIVATED && (
            <Tag color="default">Deactivated</Tag>
          )}
        </Flex>
      ),
    },
    {
      title: 'Partner Name',
      dataIndex: 'partnerName',
      key: 'partnerName',
      width: 180,
      ellipsis: true,
      render: (value?: string) => value || '-',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 220,
      ellipsis: true,
    },
    {
      title: 'Referral Code',
      dataIndex: 'code',
      key: 'code',
      width: 170,
      render: (value: string | undefined, record) => {
        if (!value) return null

        return (
          <Flex align="center" gap={4}>
            <span>{value}</span>
            <Tooltip title="View referral performance">
              <Button
                type="text"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => setPerformanceAddress(record.walletAddress)}
              />
            </Tooltip>
          </Flex>
        )
      },
    },
    {
      title: 'Total Referrals',
      dataIndex: 'totalReferral',
      key: 'totalReferral',
      width: 140,
      align: 'center',
      sorter: true,
      render: (value?: number) => value ?? 0,
    },
    {
      title: 'Total Earned',
      dataIndex: 'totalEarned',
      key: 'totalEarned',
      width: 150,
      align: 'center',
      sorter: true,
      render: (value?: number) => `${(value ?? 0).toLocaleString('en-US')} MEY`,
    },
    {
      title: 'Total Claimed',
      dataIndex: 'totalClaimed',
      key: 'totalClaimed',
      width: 150,
      align: 'center',
      sorter: true,
      render: (value?: number) => `${(value ?? 0).toLocaleString('en-US')} MEY`,
    },
    {
      title: 'Source File',
      dataIndex: 'fileName',
      key: 'fileName',
      width: 200,
      ellipsis: true,
      render: (value?: string) => value || '-',
    },
    {
      title: 'Added Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      sorter: true,
      defaultSortOrder: 'descend',
      render: (value?: string) => (value ? dayjs(value).format('DD/MM/YYYY') : '-'),
    },
  ]

  if (canEdit) {
    columns.push({
      title: 'Actions',
      key: 'actions',
      width: 90,
      fixed: 'right',
      align: 'center',
      render: (_, record) => {
        if (record.status === PartnerReferralStatus.DEACTIVATED) {
          return (
            <Tooltip title="Reactivate">
              <Button
                type="text"
                icon={<ReloadOutlined />}
                style={{ color: '#52c41a' }}
                onClick={() => handleReactivate(record)}
              />
            </Tooltip>
          )
        }

        // Active wallet: deactivate when it has referrals, otherwise allow delete.
        if (getTotalReferrals(record) > 0) {
          return (
            <Tooltip title="Deactivate">
              <Button
                type="text"
                icon={<StopOutlined />}
                style={{ color: '#fa8c16' }}
                onClick={() => openModal('deactivate', record)}
              />
            </Tooltip>
          )
        }

        return (
          <Tooltip title="Delete">
            <Button
              type="text"
              icon={<DeleteOutlined />}
              style={{ color: '#ff4d4f' }}
              onClick={() => openModal('delete', record)}
            />
          </Tooltip>
        )
      },
    })
  }

  return (
    <Flex vertical gap={16}>
      {canEdit && <PartnerWhitelistUpload onUploaded={handleUploaded} />}

      <div className="partner-referral__list-header">
        <Flex align="center" gap={12}>
          <span className="partner-referral__total">{total} total</span>
          {lastUpload && (
            <Button
              size="small"
              icon={<DownloadOutlined />}
              loading={exporting}
              onClick={handleExportUpload}
            >
              Export “{lastUpload.fileName}”
            </Button>
          )}
        </Flex>
        <Input.Search
          placeholder="Search by address, email, code or file..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          allowClear
          maxLength={PARTNER_SEARCH_MAX_LENGTH}
          className="partner-referral__search"
        />
      </div>

      <Table<PartnerReferralWhitelistItem>
        columns={columns}
        dataSource={users}
        rowKey={(record) => record.id || record.walletAddress}
        loading={isLoading}
        pagination={initPagination(total)}
        onChange={handleTableChange}
        rowClassName={(record) =>
          record.status === PartnerReferralStatus.DEACTIVATED
            ? 'partner-referral__row--deactivated'
            : ''
        }
        scroll={{ x: 1200 }}
        bordered
      />

      <PartnerActionModal
        open={!!actionRecord}
        type={actionType}
        record={actionRecord}
        loading={actionLoading}
        onConfirm={handleConfirmAction}
        onClose={() => (actionLoading ? undefined : setActionRecord(null))}
      />

      {!!performanceAddress && (
        <ModalPartnerTransactions
          walletAddress={performanceAddress}
          onClose={() => setPerformanceAddress(undefined)}
        />
      )}
    </Flex>
  )
}
